import { Bell, Menu as MenuIcon, Moon, Search, Sun } from 'lucide-react';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { Button } from '../components/ui/Button';
import { initials } from '../lib/format';
import { colorFor } from '../lib/format';
import { Badge } from '../components/ui/Badge';

/** Top bar: page-level search, theme switch, notifications and the user chip. */
/** `aria-expanded` reflects the profile popover state held by AppShell. */
export function Topbar({ onOpenMobileNav, onOpenPalette, onOpenProfile, profileOpen }: {
  onOpenMobileNav: () => void; onOpenPalette: () => void; onOpenProfile: () => void;
  profileOpen: boolean; onCloseProfile: () => void;
}) {
  const user = useAuth((s) => s.user);
  const { theme, setTheme } = useUi();

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-2 border-b border-line bg-surface-1/90 px-3 backdrop-blur sm:px-6">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onOpenMobileNav} aria-label="Open navigation">
        <MenuIcon className="h-5 w-5" />
      </Button>

      {/* Search - opens the command palette */}
      <button
        onClick={onOpenPalette}
        className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-line bg-surface-0 px-3 text-start text-sm text-ink-faint transition-colors hover:border-brand-400 hover:text-ink-muted sm:max-w-md"
      >
        <Search className="h-4 w-4 shrink-0" aria-hidden />
        <span className="truncate">Search menus, orders, people…</span>
        <kbd className="ms-auto hidden shrink-0 rounded border border-line bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-ink-muted sm:inline">
          Ctrl K
        </kbd>
      </button>

      <div className="ms-auto flex items-center gap-1">
        <Button
          variant="ghost" size="icon" aria-label="Toggle colour theme"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        <Button variant="ghost" size="icon" aria-label="Notifications" className="relative">
          <Bell className="h-5 w-5" />
          {/* Placeholder until Phase 2 wires real low-stock/order alerts. */}
          <span className="absolute end-2 top-2 h-2 w-2 rounded-full bg-brand-500" aria-hidden />
        </Button>

        <button
          onClick={onOpenProfile}
          className="ms-1 flex min-h-touch items-center gap-2 rounded-lg px-1.5 py-1 transition-colors hover:bg-surface-2"
          aria-haspopup="menu"
          aria-expanded={profileOpen}
        >
          <span
            className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{ backgroundColor: colorFor(user?.roleKey ?? 'user') }}
            aria-hidden
          >
            {initials(user?.fullName ?? '?')}
          </span>
          <span className="hidden min-w-0 text-start sm:block">
            <span className="block truncate text-sm font-medium text-ink">{user?.fullName}</span>
            <Badge tone="brand" className="mt-0.5">{user?.roleName}</Badge>
          </span>
        </button>
      </div>
    </header>
  );
}
